import type { ComponentType } from "react";
import { AlertTriangle, CalendarDays, Clock, Cog, FileText, LayoutList, User, Users } from "lucide-react";
import { cn } from "@/lib/cn";
import type { PainelChave } from "../painel/painel-tipos";
import { ROTULO_DO_PAINEL } from "./AgirNaOrganizacao";
import type { OrgDoPainel } from "./painel";

type Preview = { teamCount: number | null; patientCount: number | null; consultasMarcadasCount: number | null } | undefined;

const ICONE: Record<PainelChave, ComponentType<{ className?: string }>> = {
  equipe: Users,
  pacientes: User,
  agenda: CalendarDays,
  protese: FileText,
  pendentes: Clock,
  producao: Cog,
  atrasados: AlertTriangle,
  resumo: LayoutList,
};

/** O número que acompanha cada área ("Pacientes 21", "Agenda 2 marcadas"), dos dados que a linha
 *  já tem. Atrasado maior que zero vem em vermelho. */
function numeroDe(p: PainelChave, org: OrgDoPainel, preview: Preview): { valor: number; legenda?: string } | null {
  const l = org.laboratorio;
  const valores: Record<PainelChave, number | null | undefined> = {
    equipe: preview?.teamCount,
    pacientes: preview?.patientCount,
    agenda: preview?.consultasMarcadasCount,
    protese: org.clinica?.pedidosEmAberto,
    pendentes: l?.aguardandoAceite,
    producao: l?.emProducao,
    atrasados: l?.atrasados,
    resumo: l && l.aguardandoAceite != null && l.emProducao != null ? l.aguardandoAceite + l.emProducao : null,
  };
  const legenda: Partial<Record<PainelChave, string>> = { agenda: "marcadas", protese: "em aberto", resumo: "em aberto" };
  const v = valores[p];
  return v == null ? null : { valor: v, legenda: legenda[p] };
}

type Props = { paineis: PainelChave[]; org: OrgDoPainel; preview: Preview; aberto: PainelChave | null; onEscolher: (p: PainelChave) => void };

/** "Agir sem trocar": uma área por botão, grande o bastante pra ser a ação da linha (44px, ícone,
 *  número). Lado a lado no computador, duas colunas no celular. */
export function BotoesDeAgir({ paineis, org, preview, aberto, onEscolher }: Props) {
  return (
    <div className={cn("grid grid-cols-2 gap-2", paineis.length > 4 ? "sm:grid-cols-4 xl:grid-cols-8" : "sm:grid-cols-4")}>
      {paineis.map((p) => {
        const Icone = ICONE[p];
        const n = numeroDe(p, org, preview);
        const marcado = aberto === p;
        const alerta = p === "atrasados" && !!n?.valor;
        return (
          <button
            key={p}
            type="button"
            aria-pressed={marcado}
            onClick={() => onEscolher(p)}
            className={cn(
              "flex min-h-[48px] min-w-0 items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors",
              marcado
                ? "border-brand-600 bg-brand-600 text-white shadow-sm"
                : "border-zinc-200 bg-white text-zinc-800 hover:border-brand-300 hover:bg-brand-50/40 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted/40",
            )}
          >
            <span
              className={cn(
                "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
                marcado ? "bg-white/15" : alerta ? "bg-red-50 text-red-600 dark:bg-red-500/10" : "bg-brand-50 text-brand-600 dark:bg-primary/10 dark:text-primary",
              )}
            >
              <Icone className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold">{ROTULO_DO_PAINEL[p]}</span>
              {n && (
                <span className={cn("block truncate text-[11px] tabular-nums", marcado ? "text-white/80" : alerta ? "font-semibold text-red-600" : "text-zinc-500 dark:text-muted-foreground")}>
                  {n.valor}
                  {n.legenda ? ` ${n.legenda}` : ""}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
